import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// @mui material components
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

// Otis Admin PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function ensureArray(val) {
  return Array.isArray(val) ? val : [];
}

function CityMultiSelectionModal({ open, onClose, options, value, onChange, coversEverywhere, onCoversEverywhereChange }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState(ensureArray(value));
  const [customValue, setCustomValue] = useState("");
  const [localEvery, setLocalEvery] = useState(Boolean(coversEverywhere));

  useEffect(() => {
    if (open) {
      setSelected(ensureArray(value));
      setSearchQuery("");
      setCustomValue("");
      setLocalEvery(Boolean(coversEverywhere));
    }
  }, [open, value, coversEverywhere]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((o) =>
      String(o.label || o.value || "").toLowerCase().includes(q)
    );
  }, [options, searchQuery]);

  const labelMap = useMemo(() => {
    const m = new Map();
    (options || []).forEach((o) => m.set(o.value, o.label));
    return m;
  }, [options]);

  const toggle = (v) => {
    setSelected((prev) => {
      const set = new Set(prev);
      if (set.has(v)) set.delete(v);
      else set.add(v);
      return Array.from(set);
    });
  };

  const applyCustom = () => {
    const txt = customValue.trim();
    if (!txt) return;
    setSelected((prev) => Array.from(new Set([...prev, txt])));
    setCustomValue("");
  };

  const handleConfirm = () => {
    onCoversEverywhereChange?.(localEvery);
    onChange(selected);
    onClose();
  };

  const clearAll = () => setSelected([]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
      disableEscapeKeyDown
      disableAutoFocus
    >
      <Card
        sx={{
          width: { xs: "95%", sm: "85%", md: "70%", lg: "60%" },
          maxWidth: 700,
          maxHeight: "90vh",
          overflow: "auto",
          p: 3,
          position: "relative",
          zIndex: 10000,
          bgcolor: "#1b1d22",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <MDBox>
          <MDTypography variant="h4" mb={1} sx={{ color: "#fff" }}>
            {t("modals.cityMulti.title")}
          </MDTypography>
          <MDTypography variant="body2" mb={2} sx={{ color: "rgba(255,255,255,0.80)" }}>
            {t("modals.cityMulti.subtitle")}
          </MDTypography>

          {/* Covers everywhere checkbox */}
          <MDBox mb={2} p={1.5} sx={{ border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2, bgcolor: "rgba(255,255,255,0.04)" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localEvery}
                  onChange={(e) => setLocalEvery(e.target.checked)}
                  sx={{ color: "rgba(255,255,255,0.6)", "&.Mui-checked": { color: "#fff" } }}
                />
              }
              label={
                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#fff" }}>
                  {t("modals.cityMulti.coversEverywhere")}
                </MDTypography>
              }
              sx={{ m: 0 }}
            />
          </MDBox>

          {/* Search + bubbles + selected — hidden when coversEverywhere */}
          {!localEvery && <>
          <MDBox mb={2}>
            <TextField
              fullWidth
              placeholder={t("modals.citySelect.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "rgba(255,255,255,0.85)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                  "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
                  "&.Mui-focused fieldset": { borderColor: "#fff" },
                },
                "& .MuiOutlinedInput-input::placeholder": { color: "rgba(255,255,255,0.65)" },
              }}
            />
          </MDBox>

          {/* Bubble Layout */}
          <MDBox
            mb={2}
            mt={2}
            p={2}
            minHeight={200}
            maxHeight={350}
            overflow="auto"
            sx={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: 2 }}
          >
            {filtered.length > 0 ? (
              <MDBox display="flex" flexWrap="wrap" gap={1.5}>
                {filtered.map((opt) => {
                  const isSelected = selectedSet.has(opt.value);
                  return (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      onClick={() => toggle(opt.value)}
                      sx={{
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        padding: "3px 8px",
                        height: "auto",
                        transition: "all 0.2s",
                        color: "#fff !important",
                        bgcolor: isSelected ? "rgba(255,255,255,0.42)" : "rgba(255,255,255,0.10)",
                        border: "1px solid",
                        borderColor: isSelected ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)",
                        boxShadow: isSelected ? "0 0 0 1px rgba(255,255,255,0.25) inset" : "none",
                        "& .MuiChip-label": { color: "#fff !important" },
                        "&:hover": {
                          transform: "scale(1.04)",
                          bgcolor: isSelected ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.18)",
                          borderColor: isSelected ? "#fff" : "rgba(255,255,255,0.55)",
                        },
                      }}
                    />
                  );
                })}
              </MDBox>
            ) : (
              <MDTypography variant="body2" textAlign="center" py={4} sx={{ color: "rgba(255,255,255,0.75)" }}>
                {t("modals.citySelect.noResults")}
              </MDTypography>
            )}
          </MDBox>

          {/* V2: custom city entry — commented out until v2
          <Divider sx={{ borderColor: "rgba(255,255,255,0.15)", my: 2 }} />
          <MDTypography variant="button" fontWeight="bold" mb={1} sx={{ color: "#fff" }}>
            Άλλη πόλη (custom)
          </MDTypography>
          <MDBox display="flex" gap={1.5} flexDirection={{ xs: "column", sm: "row" }} mb={2}>
            <TextField fullWidth label="Γράψε πόλη" value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              InputLabelProps={{ sx: { color: "#fff" } }}
              sx={{ "& .MuiOutlinedInput-root": { color: "#fff", "& fieldset": { borderColor: "rgba(255,255,255,0.3)" } } }}
            />
            <MDButton variant="gradient" color="info" onClick={applyCustom} sx={{ minWidth: 120 }}>
              Προσθήκη
            </MDButton>
          </MDBox>
          */}

          {/* Selected display */}
          <MDBox mb={2} p={2} sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
            <MDTypography variant="button" fontWeight="medium" sx={{ color: "#fff" }}>
              {t("modals.cityMulti.selectedCount", { count: selected.length })}
            </MDTypography>

            {selected.length > 0 ? (
              <MDBox mt={1} display="flex" flexWrap="wrap" gap={1}>
                {selected.slice(0, 12).map((v) => (
                  <Chip
                    key={v}
                    label={labelMap.get(v) || v}
                    onDelete={() => toggle(v)}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.18)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.35)",
                      "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.85)" },
                    }}
                  />
                ))}
                {selected.length > 12 ? (
                  <Chip
                    label={`+${selected.length - 12}`}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.10)",
                      color: "#fff",
                      border: "1px dashed rgba(255,255,255,0.35)",
                      fontWeight: 800,
                    }}
                  />
                ) : null}
              </MDBox>
            ) : (
              <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.70)" }}>
                {t("modals.cityMulti.noneSelected")}
              </MDTypography>
            )}
          </MDBox>
          </>}

          {/* Actions */}
          <MDBox display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
            <MDButton variant="outlined" color="light" onClick={clearAll}>
              {t("modals.cityMulti.clearAll")}
            </MDButton>

            <MDBox display="flex" gap={2}>
              <MDButton variant="outlined" color="light" onClick={onClose}>
                {t("common.cancel")}
              </MDButton>

              <MDButton variant="gradient" color="info" onClick={handleConfirm}>
                {t("common.confirm")}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </Modal>
  );
}

CityMultiSelectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  coversEverywhere: PropTypes.bool,
  onCoversEverywhereChange: PropTypes.func,
};

export default CityMultiSelectionModal;